import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { useState, useEffect, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useGetPmSummary,
  useGetPmUpcomingEvents,
  useGetPmScheduleVariance,
  useGetPmSafetyAuditSummary,
  useGetPmQualityAuditSummary,
  useGetPmOverview,
  useGetPmClosureReadiness,
  useGetPmClientPhotos,
  useGetPmCategoryPerformance,
  useGetPmProjectPlanByStatus,
  useReviewPmAttachment,
  useDeletePMClientAttachment as useDeletePMClientAttachmentMutation,
  useGetActiveProjects
} from "@/services";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  TITLE,
  VALUE,
  SUBTITLE,
  TYPE,
  CATEGORY,
  PROGRESS,
  DELAYED,
  SAFETY,
  QUALITY,
  TOTAL_PHOTOS,
  PENDING_APPROVAL,
  APPROVED,
  REJECTED,
  PROJECT_LIST,
  QC_OFFICERS,
  SAFETY_OFFICERS,
  SITE_MANAGERS,
  PROJECT_MANAGERS,
} from "./consts";
import { schema, formValues } from "./validations";
import { getRequiredFields, getProjectHealthKey } from "@/lib/utils";
import { useNavigate } from "react-router-dom";
import { toast } from "@/components";

const useDashboard = () => {
  const loggedInUserData = useSelector(
    (state: RootState) => state.currentData.loggedInUserData
  );
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isPhotoSliderOpen, setIsPhotoSliderOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const { data: rawAdminSummaryData } = useGetPmSummary();
  const { data: rawActiveProjectsApiData } = useGetActiveProjects();

  const mapHealthStatus = (status: string) => {
    return getProjectHealthKey(status);
  };

  const projectList = useMemo(() => {
    const list = Array.isArray(rawActiveProjectsApiData) ? rawActiveProjectsApiData : rawActiveProjectsApiData?.data;
    if (!list) return { data: [] };
    return {
      data: list.map((p: any) => ({
        ...p,
        name: p.name || p.projectName,
        projectId: p.projectId,
        projectPlanVersion: p.planVersion,
        status: mapHealthStatus(p.healthStatus),
      }))
    };
  }, [rawActiveProjectsApiData]);

  const [keys, setKeys] = useState<any>({
    projectList: Math.random(),
  });

  const requiredFields = getRequiredFields(schema);

  const form = useForm<formValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      [PROJECT_LIST]: "",
    },
  });

  const selectedProjectId = form.watch(PROJECT_LIST);

  useEffect(() => {
    if (!selectedProjectId && projectList?.data?.length > 0) {
      form.setValue(PROJECT_LIST, String(projectList.data[0].projectId), {
        shouldValidate: true,
        shouldDirty: false,
        shouldTouch: false,
      });
    }
  }, [projectList, selectedProjectId, form]);

  const selectedProject = useMemo(() => {
    return (
      projectList?.data?.find(
        (project: any) =>
          String(project.projectId) === String(selectedProjectId)
      ) || null
    );
  }, [projectList, selectedProjectId]);

  const { data: rawUpcomingEventsData } = useGetPmUpcomingEvents({ projectId: selectedProjectId });
  const { data: rawScheduleVarianceData } = useGetPmScheduleVariance({ projectId: selectedProjectId });
  const { data: rawSafetyAuditSummaryData } = useGetPmSafetyAuditSummary({ projectId: selectedProjectId });
  const { data: rawQualityAuditSummaryData } = useGetPmQualityAuditSummary({ projectId: selectedProjectId });
  const { data: rawProjectOverviewData } = useGetPmOverview({ projectId: selectedProjectId });
  const { data: rawClosureReadinessData } = useGetPmClosureReadiness({ projectId: selectedProjectId });
  const { data: rawClientPhotosData } = useGetPmClientPhotos({ projectId: selectedProjectId });
  const { data: rawCategoryPerformanceData } = useGetPmCategoryPerformance({ projectId: selectedProjectId });

  const { data: approvedPlanData, isFetching: isApprovedPlanFetching } = useGetPmProjectPlanByStatus(selectedProjectId, "APPROVED");

  const { mutate: reviewAttachment, isPending: isReviewingAttachment } = useReviewPmAttachment({
    onSuccess: () => {
      toast({ message: "Status Updated Successfully" });
      setIsPhotoSliderOpen(false);
      queryClient.invalidateQueries({
        queryKey: ["dashboard-pm-client-photos", selectedProjectId],
      });
    },
    onError: (error?: any) => {
      toast({
        message:
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong",
        type: "error",
      });
    }
  });

  const { mutate: deleteAttachment, isPending: isDeletingAttachment } = useDeletePMClientAttachmentMutation({
    onSuccess: () => {
      toast({ message: "Attachment Deleted Successfully" });
      setIsPhotoSliderOpen(false);
      queryClient.invalidateQueries({
        queryKey: ["dashboard-pm-client-photos", selectedProjectId],
      });
    },
    onError: (error?: any) => {
      toast({
        message:
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong",
        type: "error",
      });
    }
  });

  const handleReviewPhoto = (photo: any, action: "APPROVED" | "REJECTED" | "DELETE", reason?: string) => {
    if (action === "DELETE") {
      deleteAttachment({
        projectId: selectedProjectId,
        taskId: photo.taskId,
        attachmentId: photo.taskAttachmentId
      });
      return;
    }
    reviewAttachment({
      projectId: selectedProjectId,
      taskId: photo.taskId,
      attachmentId: photo.taskAttachmentId,
      payload: {
        action: action as "APPROVED" | "REJECTED",
        rejectionReason: reason
      }
    });
  };
  
    const handleMajorCountClick = () => {
      if (selectedProjectId && approvedPlanData?.projectPlanId) {
        navigate(
          `/pm/project-overview/${selectedProjectId}/edit/project-plan/${approvedPlanData.projectPlanId}/view`
        );
      } else if (selectedProjectId) {
        toast({ message: "No approved plan found for this project.", type: "error" });
        console.warn("No approved plan found for project:", selectedProjectId);
      }
  };
  const adminSummaryData = rawAdminSummaryData;
  const projectPlanVersion = selectedProject?.projectPlanVersion;

  const statsCards = [
    {
      [TITLE]: "Total Projects",
      [VALUE]: adminSummaryData?.totalProjects ?? 0,
      [TYPE]: "active",
    },
    {
      [TITLE]: "Active Projects",
      [VALUE]: adminSummaryData?.activeProjects ?? 0,
      [TYPE]: "risk",
    },
    {
      [TITLE]: "Pending Approvals",
      [VALUE]: adminSummaryData?.pendingApprovals ?? 0,
      [TYPE]: "approval",
    },
    {
      [TITLE]: "Ready for Planning",
      [VALUE]: adminSummaryData?.readyForPlanning ?? 0,
      [TYPE]: "completion",
    },
  ];

  const safetyCompliance = {
    major: rawSafetyAuditSummaryData?.majorNonCompliance || 0,
    minor: rawSafetyAuditSummaryData?.minorNonCompliance || 0,
  };

  const qualityControl = {
    rework: rawQualityAuditSummaryData?.reworkRequired || 0,
    majorFailure: rawQualityAuditSummaryData?.majorFailure || 0,
  };

  const workforceOverview = adminSummaryData?.workforceOverview || {
    [PROJECT_MANAGERS]: 0,
    [SITE_MANAGERS]: 0,
    [SAFETY_OFFICERS]: 0,
    [QC_OFFICERS]: 0,
  };

  const overallProgress = [
    {
      [TITLE]: "Overall Completion",
      [VALUE]: `${rawProjectOverviewData?.overallCompletion || 0}%`,
      [TYPE]: "overall",
    },
    {
      [TITLE]: "Total Tasks",
      [VALUE]: rawProjectOverviewData?.totalTasks || 0,
      [TYPE]: "total",
    },
    {
      [TITLE]: "Delayed Tasks",
      [VALUE]: rawProjectOverviewData?.delayedTasks || 0,
      [TYPE]: "delayed",
    },
    {
      [TITLE]: "Blocked-Safety",
      [VALUE]: rawProjectOverviewData?.blockedSafety || 0,
      [TYPE]: "blocked",
    },
    {
      [TITLE]: "Blocked-Quality",
      [VALUE]: rawProjectOverviewData?.blockedQuality || 0,
      [TYPE]: "quality",
    },
  ];

  const categoryPerformance = rawCategoryPerformanceData?.map((item: any) => ({
    [CATEGORY]: item.categoryName,
    [PROGRESS]: item.completionPercent,
    [SUBTITLE]: `Last Updated: ${item.lastUpdatedAt ? new Date(item.lastUpdatedAt).toLocaleDateString("en-GB") : "No Update"}`,
    [DELAYED]: item.delayedTasks,
    [SAFETY]: item.blockedSafety,
    [QUALITY]: item.blockedQuality,
  })) || [];

  const clientSharedPhotos = useMemo(() => {
    if (!rawClientPhotosData || rawClientPhotosData.length === 0) {
      return [
        {
          [CATEGORY]: "General",
          [TOTAL_PHOTOS]: 0,
          [PENDING_APPROVAL]: 0,
          [APPROVED]: 0,
          [REJECTED]: 0,
          photos: [],
        },
      ];
    }

    return [
      {
        [CATEGORY]: "General",
        [TOTAL_PHOTOS]: rawClientPhotosData.length,
        [PENDING_APPROVAL]: rawClientPhotosData.filter((p: any) => p.approvalStatus === "PENDING").length,
        [APPROVED]: rawClientPhotosData.filter((p: any) => p.approvalStatus === "APPROVED").length,
        [REJECTED]: rawClientPhotosData.filter((p: any) => p.approvalStatus === "REJECTED").length,
        photos: rawClientPhotosData.map((p: any) => ({
          url: p.presignedUrl,
          description: p.description || "No description provided",
          taskId: p.taskId,
          taskAttachmentId: p.taskAttachmentId,
          approvalStatus: p.approvalStatus,
          reviewedByName: p.reviewedByName,
          reviewedAt: p.reviewedAt,
          rejectionReason: p.rejectionReason,
        })),
      },
    ];
  }, [rawClientPhotosData]);

  const siteManagerLastUpdate = useMemo(() => {
    if (!rawCategoryPerformanceData || rawCategoryPerformanceData.length === 0) return "No Update Found";
    const dates = rawCategoryPerformanceData
      .map((item: any) => item.lastUpdatedAt)
      .filter((date: any) => !!date)
      .map((date: any) => new Date(date).getTime());
    if (dates.length === 0) return "No Update Found";
    const maxDate = new Date(Math.max(...dates));
    return maxDate.toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  }, [rawCategoryPerformanceData]);

  const selectedGroup = useMemo(() => {
    return clientSharedPhotos?.find(
      (item: any) => item[CATEGORY] === selectedCategory
    );
  }, [clientSharedPhotos, selectedCategory]);

  const selectedPhotos = selectedGroup?.photos || [];

  const upcomingEvents = rawUpcomingEventsData || [];
  const scheduleVariance = rawScheduleVarianceData;
  const closureReadiness = rawClosureReadinessData;

  return {
    loggedInUserData,
    statsCards,
    safetyCompliance,
    qualityControl,
    workforceOverview,
    overallProgress,
    categoryPerformance,
    projectList,
    selectedProjectId,
    selectedProject,
    clientSharedPhotos,
    siteManagerLastUpdate,
    upcomingEvents,
    scheduleVariance,
    closureReadiness,
    keys,
    requiredFields,
    setKeys,
    form,
    isPhotoSliderOpen,
    setIsPhotoSliderOpen,
    selectedCategory,
    setSelectedCategory,
    isApprovedPlanLoading: isApprovedPlanFetching,
    handleMajorCountClick,
    selectedPhotos,
    handleReviewPhoto,
    isReviewingAttachment,
    isDeletingAttachment,
    projectPlanVersion
  };
};

export default useDashboard;